'use strict';

/**
 * Answer sync — dual-write bridge from the DOM form into the AnswerStore.
 *
 * The DOM stays authoritative: this is a shadow copy, not a takeover. Answers
 * reach the store through two disjoint paths:
 *
 *   1. USER edits — the browser fires native `change`/`input`, caught by one
 *      delegated listener on `document`. This is the primary path.
 *   2. PROGRAMMATIC edits — code calling jQuery `.val()` / `.prop()` / `.attr()`
 *      fires no DOM event, so those setters are wrapped to notify the store.
 *
 * Both funnel through `syncField`, which reuses the global `get_form_value`
 * (radio/checkbox/select branching) so the store mirrors exactly what submit
 * would scrape. Nothing here touches native DOM prototypes: the wrap is on
 * jQuery methods only, which React never calls, so it is safe to keep through
 * the React migration and removes cleanly once no jQuery widgets remain.
 *
 * Raw native writes (`el.value = …` / `el.checked = …`) that bypass jQuery are
 * NOT captured here by design — that is a small, known set fixed at source, and
 * anything QSL does is surfaced by RR-4 telemetry.
 *
 * Neither path can see an answer that arrived as server-rendered markup and is
 * never edited. Two COMPLEMENTARY mechanisms cover that, and neither replaces
 * the other:
 *
 *   - `utils/lib/answer-seed.js` builds the page's initial answer state from the
 *     API response at `init_page_state`. It covers `question.answer` on VISIBLE
 *     controls, which nothing else can see.
 *   - `seedHiddenAnswers()` below reads HIDDEN inputs out of the rendered DOM.
 *     It covers the answer channels the response cannot express at all — nested
 *     `category_options[].answer` (Scale, Placement), template literals
 *     (`imagemap` "unselected", `audio`/`video` "1") and template-computed
 *     counts (`matrix` row_count).
 *
 * They overlap only on `passthru`. Deleting `seedHiddenAnswers()` because
 * `answer-seed.js` "replaces" it loses six widgets' worth of coverage — that
 * happened once already; see `aiDocs/RPI-984_REMEDIATION_PLAN.md` §1.
 *
 * Patch ownership: this is the only module that reassigns `$.fn.val`/`prop`/`attr`.
 * `utils/lib/resize.js` swaps `$.fn.width` and restores the original afterwards —
 * that save-and-restore shape applied to these three would silently uninstall the
 * wrap. Coordinate here before adding another patch to them.
 */

import { report } from '../../services/report';

// `syncField` runs on every `change` AND `input`, i.e. once per keystroke. An
// error that recurs there (a page mid-transition, a replaced form) would emit
// one New Relic error per character typed. Report the first failure per page
// and stay quiet after that: the signal is "this page has a broken sync", and
// one event carries it.
var lastFailureReportedFor = null;

function reportSyncFailure(e) {
  var page;
  try {
    page = (typeof page_state !== 'undefined' && page_state.this_page) || '';
  } catch (pageLookupError) {
    page = '';
  }
  if (lastFailureReportedFor === page) {
    return;
  }
  lastFailureReportedFor = page;
  report.logError(e, true);
}

// jQuery prop/attr names that carry answer state. A prop/attr setter call is
// only synced when it touches one of these; every other prop is a passthrough.
var RELEVANT_PROPS = { checked: true, selected: true, value: true };

/**
 * Membership index for `page_state.allvars`, rebuilt only when the array changes.
 *
 * `answerKeyFor` runs on every jQuery value-setter call, so a linear scan here is
 * quadratic on any action that updates many controls at once (e.g. an argument-less
 * `$.uniform.update()`). `page_state.allvars` is assigned in exactly one place
 * (`globals/page.js` `discover_variables`) and is never mutated in place, so caching
 * on array identity is safe: a new question assigns a new array and the index
 * rebuilds. Prototype-free, so a variable named `constructor` or `toString` cannot
 * read as present.
 *
 * @returns {?Object} Map of declared variable names to `true`, or `null`.
 */
var cachedAllvars = null;
var cachedAllvarsIndex = null;

function allvarsIndex() {
  var arr =
    typeof page_state !== 'undefined' && page_state ? page_state.allvars : null;
  if (!arr) {
    cachedAllvars = null;
    cachedAllvarsIndex = null;
    return null;
  }
  if (arr !== cachedAllvars) {
    cachedAllvars = arr;
    cachedAllvarsIndex = Object.create(null);
    for (var i = 0; i < arr.length; i++) {
      cachedAllvarsIndex[arr[i]] = true;
    }
  }
  return cachedAllvarsIndex;
}

/**
 * Resolve the answer variable a control maps to.
 *
 * `get_form_value`/submit read `document.forms[0][key]`, which resolves by
 * element `id` OR `name`. Some widgets (e.g. Address) render `name` as the
 * short field name while the declared answer var — the entry in
 * `page_state.allvars` — is the prefixed `id`. So the variable is whichever of
 * the control's `name`/`id` is present in `allvars` (name wins if both match).
 *
 * @param {Element} el The form control.
 * @returns {?string} The allvars key, or `null` if the control isn't an answer.
 */
function answerKeyFor(el) {
  var index = allvarsIndex();
  if (!el || index === null) {
    return null;
  }
  if (el.name && index[el.name] === true) {
    return el.name;
  }
  if (el.id && index[el.id] === true) {
    return el.id;
  }
  return null;
}

/**
 * Shadow one field's value into the store.
 *
 * Ignores the element when it maps to no declared answer var, or when it does
 * not live inside the survey `#main_form`. Self-guarded: a sync failure must
 * never break user input or a `.val()` caller, so errors are reported silently.
 *
 * @param {Element} el The form control whose value changed.
 */
function syncField(el) {
  try {
    // Cheapest guard first: the delegated listener is scoped by its selector but
    // the setter wrap is not, so most calls arriving here are non-survey elements
    // and must be rejected before any key lookup. `getElementById` + `contains`
    // are native and beat `closest()` selector matching. Looked up per call rather
    // than cached, so a rebuilt DOM cannot leave a stale node behind.
    if (!el || el.nodeType !== 1) {
      return;
    }
    var form = document.getElementById('main_form');
    if (!form || !form.contains(el)) {
      return;
    }
    var key = answerKeyFor(el);
    if (key === null) {
      return;
    }
    Gryphon.answers.set(key, get_form_value(key));
  } catch (e) {
    reportSyncFailure(e);
  }
}

/**
 * Sync every element in a jQuery collection (used by the setter wraps).
 * @param {Object} $els A jQuery collection.
 */
function syncCollection($els) {
  for (var i = 0; i < $els.length; i++) {
    syncField($els[i]);
  }
}

/**
 * Delegated handler for native `change`/`input` (user edits).
 * @param {Event} event
 */
function handleFormChange(event) {
  syncField(event.target);
}

/**
 * Was a `.prop()` / `.attr()` call a setter that touched an answer property?
 * Handles the setter signatures `(name, value)` and `({ name: value, … })`.
 * @param {IArguments} args
 * @returns {boolean}
 */
function isRelevantPropSetter(args) {
  if (args.length >= 2 && typeof args[0] === 'string') {
    return RELEVANT_PROPS.hasOwnProperty(args[0]);
  }
  if (args.length === 1 && args[0] && typeof args[0] === 'object') {
    for (var key in args[0]) {
      if (args[0].hasOwnProperty(key) && RELEVANT_PROPS.hasOwnProperty(key)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Wrap jQuery's value setters so programmatic writes reach the store.
 *
 * Each wrapper runs the original first (preserving its return value, so
 * chaining is unaffected) and only then syncs. Idempotent: guarded so a second
 * `initAnswerSync()` cannot double-wrap. Only jQuery instance methods are
 * touched — never DOM prototypes.
 */
function patchJQuerySetters() {
  if ($.fn.val.__answerSyncWrapped) {
    return;
  }

  var origVal = $.fn.val;
  var patchedVal = function () {
    var ret = origVal.apply(this, arguments);
    // Setter forms have at least one argument; the getter (no args) is skipped.
    if (arguments.length > 0) {
      syncCollection(this);
    }
    return ret;
  };
  patchedVal.__answerSyncWrapped = true;
  $.fn.val = patchedVal;

  ['prop', 'attr'].forEach(function (method) {
    var orig = $.fn[method];
    var patched = function () {
      var ret = orig.apply(this, arguments);
      if (isRelevantPropSetter(arguments)) {
        syncCollection(this);
      }
      return ret;
    };
    patched.__answerSyncWrapped = true;
    $.fn[method] = patched;
  });
}

/**
 * Seed the store from server-rendered hidden inputs.
 *
 * A hidden input is invisible to BOTH sync paths above: no `change`/`input`
 * event fires for one, and most widgets never call `.val()` on one either. So
 * whatever the template wrote into `value=` is what submit sends and what the
 * store would otherwise miss entirely.
 *
 * This is the only channel that recovers values with no response-side source:
 *
 *   - nested per-item answers — `Scale/scale.hbs:50` and
 *     `Placement/placement.hbs:149` read `category_options[].answer`, whose
 *     variable name is not derivable from the flat `res.variables`
 *   - template literals — `_shared/imagemap/base.hbs:8` renders
 *     `value="unselected"` for an unanswered area; `Audio/audio.hbs:30` and
 *     `Video/video.hbs:7` render `value="1"` as a "reached this question" marker
 *   - template-computed counts — `Matrix/matrix-table.hbs:86-87`
 *   - `question.passthru` output, which `answer-seed.js` also covers
 *
 * Reads through `syncField`, i.e. the same `get_form_value` submit uses, so the
 * stored value is byte-identical to the DOM's. Must run after widgets have
 * rendered; idempotent, and the store dedups unchanged values, so a later real
 * write always wins.
 *
 * DELETION CRITERION, since this was deleted once on the assumption it was
 * redundant: it is safe to remove when every variable it seeds is also seeded
 * from the response, which is measurable rather than arguable — nothing it adds
 * over `answer-seed.js` on any page. Until then it is load-bearing.
 */
export function seedHiddenAnswers() {
  try {
    var hidden = $('#main_form input[type="hidden"]');
    for (var i = 0; i < hidden.length; i++) {
      syncField(hidden[i]);
    }
  } catch (e) {
    reportSyncFailure(e);
  }
}

/**
 * Bind the user-edit listener and install the programmatic-write wraps.
 *
 * Called once at bootstrap, but idempotent, and without module state: the
 * namespaced `.off()` removes only this module's handlers, so a second call
 * replaces rather than stacks and every other module's `change`/`input` binding is
 * left alone.
 */
export function initAnswerSync() {
  $(document)
    .off('change.answerSync input.answerSync')
    .on(
      'change.answerSync input.answerSync',
      '#main_form :input',
      handleFormChange
    );
  patchJQuerySetters();
}
